import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import OwnerMenu from "./OwnerMenu";
import { useTranslation } from "react-i18next";

const OwnerBlogForm = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();       // present when editing
  const navigate = useNavigate();
  const isEdit = Boolean(slug);

  const [form, setForm] = useState({
    language: ((i18n.language || "en").slice(0, 2).toLowerCase()),
    title: "",
    content: "",
    tags: "",
    status: "draft",
    meta_title: "",
    meta_description: "",
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [existingCoverUrl, setExistingCoverUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const [previewContent, setPreviewContent] = useState(false);
  const fileInputRef = useRef(null);

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_URL || "http://localhost:8000";

  const getAuthToken = () => localStorage.getItem("token");

  // Count characters for meta_description (recommended ≤ 160)
  const metaDescCount = form.meta_description.length;
  const metaTitleCount = form.meta_title.length;

  // Load article data when editing
  useEffect(() => {
    // Redirect if not owner/staff
    const rawGroups = localStorage.getItem("groups");
    let groups = [];
    if (rawGroups) {
      try {
        const parsed = JSON.parse(rawGroups);
        groups = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        groups = [rawGroups];
      }
    }
    if (!groups.includes("Owners") && !groups.includes("Staff")) {
      navigate("/login");
      return;
    }

    if (!isEdit) return;
    const fetchArticle = async () => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/blog/articles/${slug}/`,
          { headers: { Authorization: `Token ${getAuthToken()}` } }
        );
        if (!response.ok) throw new Error("Not found");
        const data = await response.json();
        setForm({
          language: data.language || "en",
          title: data.title || "",
          content: data.content || "",
          tags: Array.isArray(data.tags) ? data.tags.join(", ") : "",
          status: data.status || "draft",
          meta_title: data.meta_title || "",
          meta_description: data.meta_description || "",
        });
        if (data.cover_image_url) setExistingCoverUrl(data.cover_image_url);
      } catch {
        setError(t("error_fetching_posts"));
      } finally {
        setFetchLoading(false);
      }
    };
    fetchArticle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError("Title and content are required.");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Use FormData to support file upload
      const formData = new FormData();
      formData.append("language", form.language);
      formData.append("title", form.title);
      formData.append("content", form.content);
      formData.append("status", form.status);
      formData.append("meta_title", form.meta_title);
      formData.append("meta_description", form.meta_description);
      // tags: comma-separated string
      if (form.tags.trim()) {
        formData.append("tags", form.tags.trim());
      }
      if (coverFile) {
        formData.append("cover_image", coverFile);
      }

      const url = isEdit
        ? `${getApiBaseUrl()}/api/blog/articles/${slug}/`
        : `${getApiBaseUrl()}/api/blog/articles/`;
      const method = isEdit ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Token ${getAuthToken()}`,
          // Do NOT set Content-Type — browser sets it with boundary for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          JSON.stringify(data) || t("failed_to_save_post")
        );
      }

      alert(t("post_saved_successfully"));
      navigate("/owner/blog");
    } catch (err) {
      setError(err.message || t("failed_to_save_post"));
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <NavBar />
        <div className="flex flex-1 pt-16">
          <OwnerMenu />
          <main className="ml-48 flex-1 p-8 flex items-center justify-center">
            <p className="text-gray-500">{t("loading")}</p>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <NavBar />
      <div className="flex flex-1 pt-16">
        <OwnerMenu />

        <main className="ml-48 flex-1 p-8">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-400 mb-4">
            <Link to="/owner/blog" className="text-blue-500 hover:underline">
              {t("blog_management")}
            </Link>
            {" / "}
            <span>{isEdit ? t("edit_post") : t("new_post")}</span>
          </div>

          <h1 className="text-2xl font-bold text-gray-800 mb-6">
            {isEdit ? t("edit_post") : t("new_post")}
          </h1>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* ── Left column (main content) ─────────────────────── */}
              <div className="lg:col-span-2 space-y-6">

                {/* Title */}
                <div className="bg-white rounded-xl shadow p-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {t("post_title")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                    placeholder={t("post_title")}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                {/* Content */}
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      {t("content_html")} <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setPreviewContent((v) => !v)}
                      className="text-xs text-blue-500 hover:underline"
                    >
                      {previewContent ? "✏️ Edit" : "👁 Preview"}
                    </button>
                  </div>

                  {previewContent ? (
                    <div
                      className="prose prose-blue max-w-none min-h-64 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 overflow-auto"
                      dangerouslySetInnerHTML={{ __html: form.content }}
                    />
                  ) : (
                    <>
                      {/* Simple formatting toolbar */}
                      <div className="flex gap-1 mb-2 flex-wrap">
                        {[
                          { label: "B", tag: "strong" },
                          { label: "I", tag: "em" },
                          { label: "H2", tag: "h2" },
                          { label: "H3", tag: "h3" },
                          { label: "UL", tag: "ul", wrap: true },
                          { label: "OL", tag: "ol", wrap: true },
                          { label: "Quote", tag: "blockquote" },
                          { label: "Link", tag: "a", attr: ' href=""' },
                        ].map(({ label, tag, wrap, attr }) => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              const el = document.getElementById("blog-content-textarea");
                              const start = el.selectionStart;
                              const end = el.selectionEnd;
                              const selected = form.content.substring(start, end);
                              const wrapped = wrap
                                ? `<${tag}>\n  <li>${selected || "item"}</li>\n</${tag}>`
                                : `<${tag}${attr || ""}>${selected}</${tag}>`;
                              const newContent =
                                form.content.substring(0, start) +
                                wrapped +
                                form.content.substring(end);
                              setForm((prev) => ({ ...prev, content: newContent }));
                            }}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium px-2 py-1 rounded border border-gray-300 transition"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <textarea
                        id="blog-content-textarea"
                        name="content"
                        value={form.content}
                        onChange={handleChange}
                        required
                        rows={18}
                        placeholder="<h2>Introduction</h2><p>Your content here...</p>"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Use the buttons above to insert HTML tags, or type/paste HTML directly.
                      </p>
                    </>
                  )}
                </div>

                {/* SEO Settings */}
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                    {t("seo_settings")}
                  </h2>

                  {/* Meta Title */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-semibold text-gray-700">
                        {t("meta_title")}
                      </label>
                      <span
                        className={`text-xs ${
                          metaTitleCount > 60
                            ? "text-red-500"
                            : metaTitleCount > 50
                            ? "text-yellow-500"
                            : "text-gray-400"
                        }`}
                      >
                        {metaTitleCount} / 60
                      </span>
                    </div>
                    <input
                      type="text"
                      name="meta_title"
                      value={form.meta_title}
                      onChange={handleChange}
                      maxLength={255}
                      placeholder="Optimized page title for search engines (50-60 chars)"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Recommended: 50–60 characters. Defaults to post title if empty.
                    </p>
                  </div>

                  {/* Meta Description */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-semibold text-gray-700">
                        {t("meta_description")}
                      </label>
                      <span
                        className={`text-xs ${
                          metaDescCount > 160
                            ? "text-red-500"
                            : metaDescCount > 140
                            ? "text-yellow-500"
                            : "text-gray-400"
                        }`}
                      >
                        {metaDescCount} / 160
                      </span>
                    </div>
                    <textarea
                      name="meta_description"
                      value={form.meta_description}
                      onChange={handleChange}
                      maxLength={500}
                      rows={3}
                      placeholder="Brief summary shown in search results (120-160 chars)"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Recommended: 120–160 characters. Also used as the post preview excerpt.
                    </p>

                    {/* Live SERP preview */}
                    {(form.meta_title || form.title) && (
                      <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <p className="text-xs text-gray-400 font-medium mb-2 uppercase tracking-wide">
                          Search result preview
                        </p>
                        <p className="text-blue-600 text-base font-medium leading-snug line-clamp-1">
                          {form.meta_title || form.title}
                        </p>
                        <p className="text-green-700 text-xs mt-0.5">
                          fewo-alegria.de › blog › ...
                        </p>
                        <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                          {form.meta_description || "No description provided."}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Right column (sidebar settings) ────────────────── */}
              <div className="space-y-6">

                {/* Publish settings */}
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                    Publish
                  </h2>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t("language")}
                    </label>
                    <select
                      name="language"
                      value={form.language}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="de">DE</option>
                      <option value="en">EN</option>
                      <option value="es">ES</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t("status")}
                    </label>
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="draft">{t("draft")}</option>
                      <option value="published">{t("published")}</option>
                    </select>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? t("loading") : t("save_post")}
                    </button>
                    <Link
                      to="/owner/blog"
                      className="w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg text-sm transition"
                    >
                      {t("cancel")}
                    </Link>
                  </div>
                </div>

                {/* Cover image */}
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                    {t("cover_image")}
                  </h2>

                  {/* Current / new preview */}
                  {(coverPreview || existingCoverUrl) && (
                    <img
                      src={coverPreview || existingCoverUrl}
                      alt="cover preview"
                      className="w-full h-36 object-cover rounded-lg mb-3"
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-500 transition"
                  >
                    {coverFile
                      ? coverFile.name
                      : t("cover_image") + " — click to upload"}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Recommended: 1200×630 px, JPG/PNG/WebP
                  </p>
                </div>

                {/* Tags */}
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                    {t("tags")}
                  </h2>
                  <input
                    type="text"
                    name="tags"
                    value={form.tags}
                    onChange={handleChange}
                    placeholder={t("tags_comma_separated")}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Separate multiple tags with commas (e.g. travel, tips, Harz)
                  </p>
                  {/* Live tag preview */}
                  {form.tags && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {form.tags
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean)
                        .map((tag) => (
                          <span
                            key={tag}
                            className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default OwnerBlogForm;
